import { useMemo } from 'react';
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { DrawingKind } from '@4cm/core/tactics/drawings';

/**
 * Transparent floor plane that catches drawing-tool pointer events
 * inside the R3F scene. Replaces the absolute-positioned SVG overlay
 * the 3D board used to rely on for drawing capture.
 *
 * Advantages over the SVG approach:
 *   1. No CTM math / axis-swap — R3F's ThreeEvent carries `event.point`
 *      in world coords already (raycaster intersection on the mesh).
 *   2. Drawings capture correctly under any camera preset / orbit
 *      angle — the pitch is always Y=0 and the raycaster always hits
 *      it from the camera direction.
 *   3. No `pointer-events: none` ↔ `auto` toggle on a screen-space
 *      element — the mesh simply mounts or doesn't.
 *
 * The mesh is invisible (`material.transparent + opacity=0` rather
 * than `visible={false}` so raycasts still register). Sized at
 * FIELD + 10 m of padding so drawings starting near the touchline
 * still capture; past that the pointer falls through to OrbitControls
 * + token drag.
 */

const PADDING_M = 5;

export interface DrawingCaptureMeshProps {
  /** Active drawing tool — only mounted when non-null so OrbitControls
   *  and token drag aren't intercepted by the capture surface. */
  activeKind: DrawingKind | null;
  pitchLengthM: number;
  pitchWidthM: number;
  onPointerDown(world: { x: number; z: number }, pointerId: number): void;
  onPointerMove(world: { x: number; z: number }): void;
  onPointerUp(world: { x: number; z: number }, pointerId: number): void;
  onPointerLeave(): void;
}

export function DrawingCaptureMesh({
  activeKind,
  pitchLengthM,
  pitchWidthM,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
}: DrawingCaptureMeshProps) {
  // Plane geometry sized to field + padding. Memoised so re-renders
  // (e.g. activeKind toggling between drawing kinds) don't re-allocate.
  const geometry = useMemo(
    () =>
      new THREE.PlaneGeometry(
        pitchLengthM + PADDING_M * 2,
        pitchWidthM + PADDING_M * 2,
      ),
    [pitchLengthM, pitchWidthM],
  );

  // Invisible material — `opacity=0` keeps the mesh in the raycaster's
  // hit-test path while painting nothing.
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  if (!activeKind) return null;

  const handle = (
    handler: (world: { x: number; z: number }, pointerId: number) => void,
  ) =>
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      // R3F's `e.point` is the world-space hit point on this mesh.
      // The mesh lies on the pitch plane so x + z map 1:1 to world.
      handler({ x: e.point.x, z: e.point.z }, e.pointerId);
    };

  return (
    <mesh
      geometry={geometry}
      material={material}
      position={[0, 0.001, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={-1}
      onPointerDown={handle(onPointerDown)}
      onPointerMove={(e) => {
        e.stopPropagation();
        onPointerMove({ x: e.point.x, z: e.point.z });
      }}
      onPointerUp={handle(onPointerUp)}
      onPointerLeave={() => onPointerLeave()}
      onPointerCancel={handle(onPointerUp)}
    />
  );
}
